import React, { useState, useEffect } from 'react'
import { IconButton, makeStyles, MobileStepper } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'

const useStyles = makeStyles<Theme, CarouselProps>(
  (theme) => ({
    root: {},
    swipeContainer: {
      overflow: 'hidden',
      position: 'relative',
      flexGrow: 1,
      display: 'flex'
    },
    // the active slide's display comes entirely from this class (the inline style
    // only sets display: none on hidden slides) — consumers overriding classes.slide
    // with a different display (e.g. ViewMediaDialog's block for scrollable PDFs)
    // must provide one
    slide: {
      display: 'flex',
      width: '100%',
      justifyContent: 'center'
    },
    mobileStepper: {
      ...theme.typography.h3
    }
  }),
  { name: 'Carousel' }
)

export interface CarouselProps {
  classes?: StyleClasses<typeof useStyles>
  index?: number
  children: React.ReactNode[]
  getActiveIndex?: React.Dispatch<React.SetStateAction<number>>
  beforeStepper?: React.ReactNode
  handleSwitching?: () => void
  handleTransitionEnd?: () => void
}

function Carousel(props: CarouselProps) {
  const classes = useStyles(props)
  const { children, getActiveIndex, index: propsIndex, beforeStepper } = props
  const [index, setIndex] = useState(propsIndex || 0)

  useEffect(() => {
    setIndex(propsIndex || 0)
  }, [propsIndex])

  const handleSetIndex = (i: number) => {
    setIndex(i)
    if (getActiveIndex) getActiveIndex(i)
  }

  const nextButton = (
    <IconButton
      onClick={() => handleSetIndex(index + 1)}
      disabled={index === children.length - 1}
      style={{ marginLeft: 16 }}
    >
      <KeyboardArrowRightIcon />
    </IconButton>
  )

  const backButton = (
    <IconButton onClick={() => handleSetIndex(index - 1)} disabled={index === 0} style={{ marginRight: 16 }}>
      <KeyboardArrowLeftIcon />
    </IconButton>
  )

  const steps = children.length

  return (
    <>
      <div className={classes.swipeContainer}>
        {React.Children.map(children, (child, i) => (
          <div key={i} className={classes.slide} style={{ display: i === index ? undefined : 'none' }}>
            {child}
          </div>
        ))}
      </div>
      {beforeStepper}
      <MobileStepper
        variant={steps < 7 ? 'dots' : 'text'}
        className={classes.mobileStepper}
        steps={steps}
        activeStep={index}
        position="static"
        nextButton={nextButton}
        backButton={backButton}
      />
    </>
  )
}

export default Carousel
